import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Loader2, Building2, User, Workflow, FileText, HardHat, X } from "lucide-react";

const TYPE_META = {
  client: { icon: Building2, label: "Client", color: "text-cyan-600" },
  contact: { icon: User, label: "Contact", color: "text-sky-600" },
  deal: { icon: Workflow, label: "Deal", color: "text-violet-600" },
  quote: { icon: FileText, label: "Quote", color: "text-amber-600" },
  job: { icon: HardHat, label: "Job", color: "text-emerald-600" },
};

export function GlobalSearch({ className = "" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data.results || []);
        setActive(0);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Keyboard shortcut: "/" to focus
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ("");
    nav(r.link);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search clients, customer #, deals, quotes, jobs…"
          className="w-full h-10 pl-9 pr-8 rounded-sm border border-slate-200 bg-slate-50 focus:bg-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none text-sm transition-colors"
        />
        {loading && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-500 animate-spin" />}
        {!loading && q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={15} />
          </button>
        )}
      </div>

      {open && (
        <div data-testid="global-search-results" className="absolute z-50 mt-1.5 w-full sm:w-[420px] max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-xl">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              {loading ? "Searching…" : `No matches for "${q}"`}
            </div>
          ) : (
            results.map((r, i) => {
              const meta = TYPE_META[r.type] || TYPE_META.client;
              const Icon = meta.icon;
              return (
                <button
                  key={`${r.type}-${r.id}-${i}`}
                  data-testid={`search-result-${r.type}-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(r)}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-slate-100 last:border-0 transition-colors ${i === active ? "bg-cyan-50" : "hover:bg-slate-50"}`}
                >
                  <Icon size={16} className={`mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 truncate">{r.title || "—"}</span>
                      <span className="stamp-label text-[9px] text-slate-400 shrink-0">{meta.label}</span>
                    </div>
                    {r.subtitle && <div className="text-xs text-slate-500 truncate">{r.subtitle}</div>}
                  </div>
                  {r.customer_number && (
                    <span className="font-mono text-[11px] text-cyan-600 shrink-0 mt-0.5">{r.customer_number}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
